// frontend/src/components/common/BoostModal.js
import React, { useState, useEffect, useMemo } from 'react';
import { FaRocket, FaTimes, FaSpinner, FaCheckCircle, FaClock, FaCalendarAlt, FaCalendarWeek, FaRegCalendarAlt } from 'react-icons/fa';
import { premiumApi } from '../../api/premium';
import { settingsApi } from '../../api/settings';
import helpers from '../../utils/helpers';

function BoostModal({ isOpen, onClose, itemId, itemType, itemName }) {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState({});
  const [processing, setProcessing] = useState(false);
  
  // --- FLEXIBLE STATE ---
  const [selectedUnit, setSelectedUnit] = useState('24h'); // Default to Days
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    if (isOpen) {
      fetchConfig();
    }
  }, [isOpen]);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const settings = await settingsApi.getAllSettings();
      setConfig(settings.premium_config || {});
    } catch (err) {
      console.error("Failed to load pricing", err);
    } finally {
      setLoading(false);
    }
  };

  // Define the available units and their icons/labels
  const unitOptions = [
      { id: '1h', label: 'Hour', icon: FaClock, priceKey: 'boost_price_1h' },
      { id: '24h', label: 'Day', icon: FaCalendarAlt, priceKey: 'boost_price_24h' },
      { id: '7d', label: 'Week', icon: FaCalendarWeek, priceKey: 'boost_price_7d' },
      { id: '30d', label: 'Month', icon: FaRegCalendarAlt, priceKey: 'boost_price_30d' },
  ];

  // Calculate total price live
  const totalPrice = useMemo(() => {
      const activeOption = unitOptions.find(opt => opt.id === selectedUnit);
      const unitPrice = config[activeOption.priceKey] || 0;
      return unitPrice * (parseInt(quantity) || 0);
  }, [selectedUnit, quantity, config]);

  const handleBoost = async () => {
    const qty = parseInt(quantity);
    if (!qty || qty <= 0) return alert("Please enter a valid quantity.");

    setProcessing(true);
    try {
      // Send the unit and quantity to the updated backend API
      const response = await premiumApi.boostItem(itemId, itemType, selectedUnit, qty);
      
      if (response.payment_link) {
        window.location.href = response.payment_link;
      } else {
        alert("Failed to initialize payment.");
        setProcessing(false);
      }
    } catch (err) {
      alert("Payment Error: " + (err.message || "Unknown error"));
      setProcessing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-80 p-4 backdrop-blur-sm">
      
      {/* 
          FIX: Added max-h-[90vh] to ensure it fits on screen.
          FIX: Added flex flex-col to manage internal layout.
      */}
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden animate-fadeInUp relative">
        
        <button 
            onClick={onClose} 
            className="absolute top-3 right-3 text-white/80 hover:text-white z-20 bg-black/20 rounded-full p-1"
        >
            <FaTimes size={20} />
        </button>

        {loading ? (
            <div className="p-12 text-center">
                <FaSpinner className="animate-spin text-4xl text-red-600 mx-auto mb-4" />
                <p className="text-gray-500">Loading boost options...</p>
            </div>
        ) : (
            <>
                {/* HEADER - Fixed (flex-shrink-0) */}
                <div className="bg-gradient-to-r from-red-600 to-orange-600 p-6 text-center text-white flex-shrink-0">
                    <FaRocket className="text-4xl mx-auto mb-3 text-yellow-300 drop-shadow-md animate-pulse" />
                    <h2 className="text-xl font-bold mb-1">Boost Your Listing</h2>
                    <p className="text-red-100 text-xs truncate px-4">
                        Promote <strong>{itemName}</strong>
                    </p>
                </div>

                {/* CONTENT - Scrollable (overflow-y-auto) */}
                <div className="p-5 space-y-5 overflow-y-auto">
                    
                    {/* 1. UNIT SELECTOR */}
                    <div>
                        <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">1. Select Time Unit</h4>
                        <div className="grid grid-cols-2 gap-2">
                            {unitOptions.map(opt => (
                                <button
                                    key={opt.id}
                                    onClick={() => setSelectedUnit(opt.id)}
                                    className={`flex items-center gap-2 p-2 border-2 rounded-lg transition-all ${
                                        selectedUnit === opt.id 
                                        ? 'border-red-500 bg-red-50 text-red-700' 
                                        : 'border-gray-100 text-gray-600 hover:border-red-200'
                                    }`}
                                >
                                    <opt.icon className="text-lg" />
                                    <span className="font-bold text-sm">{opt.label}s</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* 2. QUANTITY INPUT */}
                    <div>
                        <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">2. How many {unitOptions.find(o => o.id === selectedUnit).label}s?</h4>
                        <div className="flex items-center gap-4">
                            <input 
                                type="number"
                                min="1"
                                value={quantity}
                                onChange={(e) => setQuantity(e.target.value)}
                                className="w-full p-3 border-2 border-gray-200 rounded-xl text-xl font-bold text-center focus:border-red-500 outline-none transition-colors"
                            />
                        </div>
                    </div>

                    {/* 3. TOTAL DISPLAY */}
                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 flex justify-between items-center">
                        <div>
                            <span className="text-xs text-gray-500 uppercase font-bold">Total Price</span>
                            <div className="text-xl font-black text-gray-900">
                                {helpers.formatCurrency(totalPrice, 'NGN')}
                            </div>
                        </div>
                        <div className="text-right">
                            <span className="text-[10px] text-gray-400 uppercase font-bold">Duration</span>
                            <div className="text-sm font-bold text-red-600">
                                {quantity} {unitOptions.find(o => o.id === selectedUnit).label}{quantity > 1 ? 's' : ''}
                            </div>
                        </div>
                    </div>

                    <button 
                        onClick={handleBoost}
                        disabled={processing || totalPrice <= 0}
                        className={`w-full py-3 rounded-xl font-black text-white shadow-lg transition-all flex items-center justify-center gap-2 ${
                            processing || totalPrice <= 0 
                            ? 'bg-gray-300 cursor-not-allowed' 
                            : 'bg-red-600 hover:bg-red-700 hover:scale-[1.02] active:scale-95'
                        }`}
                    >
                        {processing ? <FaSpinner className="animate-spin" /> : <FaCheckCircle />} 
                        Pay & Boost Now
                    </button>

                    <p className="text-[10px] text-gray-400 text-center pb-2">
                        * Boosting starts immediately after payment confirmation.
                    </p>
                </div>
            </>
        )}
      </div>
    </div>
  );
}

export default BoostModal;